import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone_number: '',
      password: '',
      confirm_password: '',
      terms: false,
    },
  });

  const password = watch('password');

  const onSubmit = async (data) => {
    const payload = {
      name: data.name.trim(),
      email: data.email.trim(),
      phone_number: data.phone_number ? `+${data.phone_number}`.replace(/^(\++)/, '+') : '',
      password: data.password,
      confirm_password: data.confirm_password,
    };

    const result = await registerUser(payload);

    if (result?.success) {
      if (result?.otpRequired) {
        toast.success('Signup successful! Please verify the OTP sent to your email.');
        navigate(`/verify-otp?email=${encodeURIComponent(data.email)}`);
      } else {
        toast.success('Registration successful!');
        navigate('/dashboard');
      }
      return;
    }

    toast.error(result?.error || 'Registration failed');
  };

  return (
    <div className="w-full">
      <div className="text-left mb-8">
        <h2 className="text-3xl font-bold text-white mb-2 leading-tight">Create Account</h2>
        <p className="text-gray-400 font-medium tracking-tight">Get started with Valoria today</p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Full Name</label>
            <div className="relative">
              <input
                {...register('name', {
                  required: 'Full name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' },
                })}
                type="text"
                className={`block w-full px-5 py-3.5 bg-[#1A1F26] border ${errors.name ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
                placeholder="John Doe"
              />
            </div>
            {errors.name && <p className="mt-1 text-xs font-semibold text-red-400 ml-1">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Email</label>
            <div className="relative">
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' },
                })}
                type="email"
                className={`block w-full px-5 py-3.5 bg-[#1A1F26] border ${errors.email ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
                placeholder="john@company.com"
              />
            </div>
            {errors.email && <p className="mt-1 text-xs font-semibold text-red-400 ml-1">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Phone Number</label>
            <Controller
              name="phone_number"
              control={control}
              rules={{ required: 'Required' }}
              render={({ field: { onChange, value } }) => (
                <PhoneInput
                  country="fr"
                  value={value}
                  onChange={(val) => onChange(val)}
                  containerClass="!w-full"
                  inputClass={`!w-full !h-[54px] !text-white !bg-[#1A1F26] !border ${errors.phone_number ? '!border-red-500' : '!border-white/5'} !rounded-[1rem] !transition-all !text-sm focus:!ring-4 focus:!ring-primary-500/10 focus:!border-primary-500`}
                  buttonClass={`!bg-transparent !border-none !rounded-l-[1rem]`}
                  dropdownClass="!bg-[#1A1F26] !text-white !shadow-2xl !border-white/10 !rounded-xl"
                />
              )}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Password</label>
            <div className="relative">
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Min 8 chars' },
                })}
                type={showPassword ? 'text' : 'password'}
                className={`block w-full px-5 pr-12 py-3.5 bg-[#1A1F26] border ${errors.password ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-bold text-gray-300 ml-1">Confirm Password</label>
            <div className="relative">
              <input
                {...register('confirm_password', {
                  required: 'Confirm password',
                  validate: (value) => value === password || 'No match',
                })}
                type={showConfirmPassword ? 'text' : 'password'}
                className={`block w-full px-5 pr-12 py-3.5 bg-[#1A1F26] border ${errors.confirm_password ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3.5 px-6 rounded-[1rem] shadow-[0_20px_40px_-12px_rgba(79,70,229,0.4)] text-base font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              'Create Account'
            )}
          </button>
        </div>

        <p className="text-center text-sm font-semibold text-gray-400 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-500 hover:text-primary-400 font-bold ml-1 transition-colors">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
};

export default Register;